import React, { useState, useEffect, memo } from 'react';

interface SearchBarProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  variant?: 'violet' | 'white';
}

const SearchBar: React.FC<SearchBarProps> = memo(({ value, onChange, placeholder = 'Search...', className = '', variant = 'violet' }) => {
  const [localSearchTerm, setLocalSearchTerm] = useState(value);

  // Update local state when prop value changes
  useEffect(() => {
    setLocalSearchTerm(value);
  }, [value]);

  // Debounce search term updates
  useEffect(() => {
    const timer = setTimeout(() => {
      if (localSearchTerm !== value) {
        onChange(localSearchTerm);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [localSearchTerm, value, onChange]);

  // Determine styles based on variant
  const styles = variant === 'white'
    ? {
        iconColor: 'text-gray-400',
        inputClass: 'block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-colors duration-200',
        clearButtonHover: 'hover:text-gray-600'
      }
    : {
        iconColor: 'text-violet-500',
        inputClass: 'block w-full pl-10 pr-3 py-2.5 border border-violet-200 rounded-lg bg-violet-100/80 shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-colors duration-200',
        clearButtonHover: 'hover:text-violet-600'
      };

  return (
    <div className={`relative ${className}`}>
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
        <svg className={`h-5 w-5 ${styles.iconColor}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
        </svg>
      </div>
      <input
        type="text"
        value={localSearchTerm}
        onChange={(e) => setLocalSearchTerm(e.target.value)}
        placeholder={placeholder}
        className={styles.inputClass}
      />
      {localSearchTerm && (
        <button
          onClick={() => setLocalSearchTerm('')}
          className={`absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 ${styles.clearButtonHover} transition-colors duration-200`}
        >
          <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
        </button>
      )}
    </div>
  );
});

export default SearchBar; 